import React, { FC, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import CreateCompany from './CreateCompany';
import Settings from './Settings';
import { ToastContainer } from 'react-toastify';
import StatusCheckWrapper from './misc/StatusCheckWrapper';
import ChatInterface from './chat/ChatInterface';
import ConversationalDashboard from './chat/ConversationalDashboard';
import ProDocView from './chat/ProDocView';
import ProDocList from './chat/ProDocList';
import DreamDaily from './chat/DreamDaily';
import MemoryArea from './chat/MemoryArea';
import { AgentProvider, useAgent } from '../contexts/AgentContext';
import { useThreadReads } from '../providers';
import './chat/conversational-ui.scss';
import ConfirmAction from './ConfirmAction';
import ExpansiveSideNavigation from './chat/ExpansiveSideNavigation/ExpansiveSideNavigation';
import BookingPage from './BookingPage';
import WebRTCVoiceChat from './WebRTCVoiceChat';
import HumanTasks from './chat/HumanTasks';
import SalesPlan from './SalesPlan';
import OrgTiers from './OrgTiers';
import { SalesInbox } from './chat/SalesInbox';
import FastChatButton from './common/FastChatButton';
import CallNotification from './common/CallNotification';
import { OnboardingModal } from './onboarding/OnboardingModal';

const ConversationalUIContent: FC = () => {
  const { loadAgents } = useAgent();
  const { totalUnreadCount } = useThreadReads();
  const location = useLocation();

  const isVoiceChat = location.pathname === '/voice-chat';
  const isDreamDaily = location.pathname === '/dream-daily';
  const isHumanTasks = location.pathname === '/tasks';
  const isBaseRoute =
    location.pathname === '/' || location.pathname.indexOf('/docs') !== -1;

  // Load agents on component mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Update document title with unread count
  useEffect(() => {
    if (totalUnreadCount > 0) {
      document.title = `(${totalUnreadCount}) Dream AI`;
    } else {
      document.title = 'Dream AI';
    }
  }, [totalUnreadCount]);

  return (
    <div
      className={`conversational-container ${isVoiceChat || isDreamDaily || isHumanTasks ? 'no-nav' : ''} ${isBaseRoute ? 'high-af' : ''}`}
    >
      {/* {!isVoiceChat && <NotificationPermissionBanner />} */}
      {!isVoiceChat && !isDreamDaily && !isHumanTasks && (
        <ExpansiveSideNavigation />
      )}
      <div className={`conversational-content`}>
        <Routes>
          <Route path='/' element={<ConversationalDashboard />} />
          <Route path='/conversations' element={<ChatInterface />} />
          <Route path='/memory' element={<MemoryArea />} />
          <Route path='/docs' element={<ProDocList />} />
          <Route path='/docs/:proDocId' element={<ProDocView />} />
          <Route path='/dream-daily' element={<DreamDaily />} />
          <Route path='/tasks' element={<HumanTasks />} />
          <Route path='/sales-plan' element={<SalesPlan />} />
          <Route path='/sales-inbox' element={<SalesInbox />} />
          <Route path='/booking/:key' element={<BookingPage />} />

          <Route
            path='/confirm/:confirmationCode'
            element={<ConfirmAction />}
          />
          <Route path='/create-company' element={<CreateCompany />} />
          <Route path='/settings' element={<Settings />} />
          <Route path='/settings/:setting' element={<Settings />} />
          <Route path='/voice-chat' element={<WebRTCVoiceChat />} />
          <Route path='/org-tiers' element={<OrgTiers />} />
          {/* Add any other routes that should be accessible in conversational mode */}
        </Routes>
      </div>
      <ToastContainer className='toast-container-wrapper' />
      <StatusCheckWrapper />
      <FastChatButton />
      <CallNotification />

      <OnboardingModal
        modalKey='new_layout'
        title="We've made it easier to find what's important"
        imageSrc='/onboarding-modals/new-layout.png'
        primaryButtonText='Got it'
      >
        <p>
          We heard you tell us that finding campaigns and chats was hard work,
          so we've made them easier to find with a new, simpler navigation.
        </p>
        <p>
          Easily see what chats have updates, and get to them directly from the
          navigation bar. Start a new chat or campaign straight from the nav
          bar.
        </p>
      </OnboardingModal>
    </div>
  );
};

const ConversationalUI: FC = () => {
  return (
    <AgentProvider>
      <ConversationalUIContent />
    </AgentProvider>
  );
};

export default ConversationalUI;
